import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "./lib";

/**
 * Purchase form — create a new PO or edit an existing draft/ordered one.
 * Adds line items backed by the parts master (or free-text if the part isn't
 * in stock yet). Live totals with GST split.
 */
export default function PurchaseForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;

  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [nextNumber, setNextNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    vendor_id: "",
    lines: [freshLine()],
    is_interstate: false,
    extra_discount: 0,
    notes: "",
    expected_date: "",
    payment_terms: "",
    invoice_number: "",
    invoice_date: "",
    place_of_supply: "",
  });

  // Bootstrap: vendors + parts + next number (or existing purchase)
  useEffect(() => {
    api.get("/spares/vendors", { params: { active_only: true } }).then((r) => setVendors(r.data || []));
    api.get("/spares/parts", { params: { limit: 1000 } }).then((r) => setParts(r.data || []));
    if (!isEdit) {
      api.get("/spares/purchases/next-number").then((r) => setNextNumber(r.data.next_number || ""));
    }
  }, [isEdit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/spares/purchases/${id}`);
      const p = r.data;
      setForm({
        vendor_id: p.vendor_id || "",
        lines: (p.lines || []).map((l) => ({ ...l })),
        is_interstate: p.is_interstate || false,
        extra_discount: p.extra_discount || 0,
        notes: p.notes || "",
        expected_date: p.expected_date || "",
        payment_terms: p.payment_terms || "",
        invoice_number: p.invoice_number || "",
        invoice_date: p.invoice_date || "",
        place_of_supply: p.place_of_supply || "",
      });
      setNextNumber(p.number);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load purchase");
      nav("/spare-parts/purchases");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { if (isEdit) load(); }, [isEdit, load]);

  const totals = useMemo(() => computeLocalTotals(form.lines, form.is_interstate, form.extra_discount), [form]);

  const setLine = (idx, patch) => setForm((f) => ({
    ...f,
    lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, freshLine()] }));
  const removeLine = (idx) => setForm((f) => ({
    ...f, lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== idx) : f.lines,
  }));
  const pickPart = (idx, part) => setLine(idx, {
    part_id: part.id, name: part.name, part_number: part.part_number || "",
    unit: part.unit || "nos", rate: part.purchase_price || 0, gst_rate: part.gst_rate ?? 18,
  });

  const save = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    const goodLines = form.lines.filter((l) => (l.name || "").trim() && Number(l.qty) > 0);
    if (goodLines.length === 0) { toast.error("Add at least one line with a name + qty"); return; }
    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id,
        lines: goodLines.map((l) => ({
          part_id: l.part_id || null,
          name: l.name, part_number: l.part_number || "",
          unit: l.unit || "nos",
          qty: Number(l.qty) || 0, rate: Number(l.rate) || 0,
          gst_rate: Number(l.gst_rate) || 0, discount_pct: Number(l.discount_pct) || 0,
          notes: l.notes || "",
        })),
        is_interstate: !!form.is_interstate,
        extra_discount: Number(form.extra_discount) || 0,
        notes: form.notes || "", expected_date: form.expected_date || null,
        payment_terms: form.payment_terms || "",
        invoice_number: form.invoice_number || "",
        invoice_date: form.invoice_date || null,
        place_of_supply: form.place_of_supply || "",
      };
      const r = isEdit
        ? await api.patch(`/spares/purchases/${id}`, payload)
        : await api.post("/spares/purchases", payload);
      toast.success(isEdit ? "Purchase updated" : `Purchase ${r.data.number} created`);
      nav(`/spare-parts/purchases/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading purchase…</div>;

  return (
    <div className="space-y-4" data-testid="purchase-form-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/spare-parts/purchases" data-testid="purchase-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">
              {isEdit ? "Edit Purchase" : "New Purchase"}
            </h1>
            <p className="text-[12.5px] text-[#667085] mt-0.5 font-mono">{nextNumber || "—"}</p>
          </div>
        </div>
        <button onClick={save} disabled={saving}
                data-testid="purchase-save-btn"
                className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <Save size={13} /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create purchase")}
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-3">
            <h3 className="text-[13px] font-bold text-[#1d3557]">Vendor & invoice</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Vendor *">
                <select value={form.vendor_id}
                        onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                        data-testid="purchase-vendor"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                  <option value="">Choose vendor…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Vendor invoice #">
                <input type="text" value={form.invoice_number}
                       onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                       data-testid="purchase-invoice-number"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
              </Field>
              <Field label="Invoice date">
                <input type="date" value={form.invoice_date || ""}
                       onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Expected date">
                <input type="date" value={form.expected_date || ""}
                       onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Payment terms">
                <input type="text" value={form.payment_terms}
                       onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                       placeholder="Net 30 / COD / Advance"
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
              <Field label="Place of supply">
                <input type="text" value={form.place_of_supply}
                       onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })}
                       className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
              </Field>
            </div>
            <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1d3557]">
              <input type="checkbox" checked={form.is_interstate}
                     onChange={(e) => setForm({ ...form, is_interstate: e.target.checked })}
                     data-testid="purchase-interstate"
                     className="w-4 h-4 accent-[#e63946]" />
              Inter-state supply (IGST instead of CGST+SGST)
            </label>
          </section>

          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[13px] font-bold text-[#1d3557]">Line items</h3>
              <button type="button" onClick={addLine} data-testid="purchase-add-line"
                      className="h-8 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                <Plus size={12} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {form.lines.map((ln, idx) => (
                <LineRow key={idx} idx={idx} ln={ln} parts={parts}
                         onChange={(patch) => setLine(idx, patch)}
                         onPart={(p) => pickPart(idx, p)}
                         onRemove={() => removeLine(idx)}
                         removable={form.lines.length > 1} />
              ))}
            </div>
          </section>

          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-3">
            <h3 className="text-[13px] font-bold text-[#1d3557]">Notes</h3>
            <textarea rows={2} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Internal notes about this purchase"
                      className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </section>
        </div>

        <aside className="space-y-4">
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="purchase-totals">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Summary</h3>
            <Row label="Subtotal" value={inr(totals.subtotal)} />
            <Row label="Line discount" value={`− ${inr(totals.line_discount)}`} muted />
            <Row label="Taxable" value={inr(totals.taxable)} />
            {form.is_interstate ? (
              <Row label="IGST" value={inr(totals.igst)} muted />
            ) : (
              <>
                <Row label="CGST" value={inr(totals.cgst)} muted />
                <Row label="SGST" value={inr(totals.sgst)} muted />
              </>
            )}
            <div className="my-2 border-t border-[#F1FAEE]" />
            <div className="flex items-center gap-2">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] flex-1">Extra discount</span>
              <input type="number" value={form.extra_discount}
                     onChange={(e) => setForm({ ...form, extra_discount: e.target.value })}
                     data-testid="purchase-extra-discount"
                     className="w-24 h-8 px-2 rounded-md border border-[#E4E7EC] text-[12px] font-mono text-right" />
            </div>
            <div className="my-2 border-t border-[#F1FAEE]" />
            <Row label="Grand total" value={inr(totals.grand_total)} big />
          </section>
        </aside>
      </div>
    </div>
  );
}

function LineRow({ idx, ln, parts, onChange, onPart, onRemove, removable }) {
  const [suggest, setSuggest] = useState(false);
  const results = ln.name
    ? parts.filter((p) => (p.name || "").toLowerCase().includes((ln.name || "").toLowerCase()) ||
                          (p.part_number || "").toLowerCase().includes((ln.name || "").toLowerCase()))
        .slice(0, 6)
    : [];
  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3 space-y-2" data-testid={`purchase-line-${idx}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-5 relative">
          <input value={ln.name} onFocus={() => setSuggest(true)}
                 onBlur={() => setTimeout(() => setSuggest(false), 200)}
                 onChange={(e) => onChange({ name: e.target.value, part_id: null })}
                 placeholder="Part name *"
                 data-testid={`purchase-line-${idx}-name`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          {suggest && results.length > 0 && (
            <ul className="absolute z-10 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg w-full max-h-52 overflow-y-auto text-[12.5px]">
              {results.map((p) => (
                <li key={p.id}>
                  <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); onPart(p); setSuggest(false); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[#F1FAEE]">
                    <div className="font-semibold text-[#1d3557]">{p.name}</div>
                    <div className="text-[10.5px] font-mono text-[#667085]">{p.part_number || ""} · ₹{p.purchase_price || 0}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input value={ln.part_number || ""} onChange={(e) => onChange({ part_number: e.target.value })}
               placeholder="Part #"
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.qty}
               onChange={(e) => onChange({ qty: Number(e.target.value) })}
               placeholder="Qty"
               data-testid={`purchase-line-${idx}-qty`}
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.rate}
               onChange={(e) => onChange({ rate: Number(e.target.value) })}
               placeholder="Rate"
               data-testid={`purchase-line-${idx}-rate`}
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.gst_rate}
               onChange={(e) => onChange({ gst_rate: Number(e.target.value) })}
               placeholder="GST%"
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <button type="button" onClick={onRemove} disabled={!removable}
                data-testid={`purchase-line-${idx}-remove`}
                className="md:col-span-1 w-full h-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40 disabled:opacity-30">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={`${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}

function freshLine() {
  return { part_id: null, name: "", part_number: "", unit: "nos", qty: 1, rate: 0, gst_rate: 18, discount_pct: 0, notes: "" };
}

function computeLocalTotals(lines, is_interstate, extra_discount) {
  let subtotal = 0, line_discount = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
  for (const ln of lines || []) {
    const qty = Number(ln.qty) || 0;
    const rate = Number(ln.rate) || 0;
    const gross = qty * rate;
    const disc = gross * (Number(ln.discount_pct) || 0) / 100;
    const tx = gross - disc;
    const gr = Number(ln.gst_rate) || 0;
    if (is_interstate) igst += tx * gr / 100;
    else { cgst += tx * gr / 200; sgst += tx * gr / 200; }
    subtotal += gross; line_discount += disc; taxable += tx;
  }
  const grand = taxable + cgst + sgst + igst - (Number(extra_discount) || 0);
  return { subtotal, line_discount, taxable, cgst, sgst, igst, grand_total: grand };
}
